import React, { useContext, useEffect, useState, useMemo } from 'react';
import { config as hopsConfig } from '@xingternal/hops';
import axios from 'axios';
import PropTypes from 'prop-types';

import { AppContext } from '../AppContext';

const PDF_ENDPOINT_BASE = 'position_details_form_support';

const defaultValue = {
  work_experiences: [],
  languages: [],
  language_levels: [],
  hasError: false,
};
const PDFContext = React.createContext(defaultValue);

// adding a `displayName` we can easily trace in the `Component tree`
PDFContext.displayName = 'PDFContext';

/**
 * Hook to retrieve mapping (X1 - PDF) for languages & language_levels
 *
 * @returns {{work_experiences:[{id:Number,name:String}],languages: [{code:Number, name: String}],language_levels: [{id:Number,name:String}], hasError: {Boolean}}} Data from PDF endpoint `languages` & `language_levels`
 */
const usePDF = () => {
  const context = useContext(PDFContext);

  if (!context) {
    throw new Error('You need a PDFProvider component to expose PDFContext');
  }

  return context;
};

/**
 * Wrap our `component` or `route` with this `Provider` in order to consume `languages`, `languages_levels` & `work_experiences` values from PDF endpoints.
 *
 * The `PDFProvider` will react if the main `user.language` changes (en|de),
 * it will not re-request if the user navigates to another `Page` (data will persist).
 *
 * @param {React.ReactNode} children
 */
const PDFProvider = ({ children }) => {
  const { externalUrl } = hopsConfig;
  const {
    data: { language },
  } = useContext(AppContext);
  const [pdfData, setPDFData] = useState(defaultValue);
  const [hasError, setHasError] = useState(false);

  /**
   * Update state with all responses from the endpoints.
   * If previously `hasError` was `true` we change it to `false`
   *
   * @param {Array.<{data:{Object}}>} responseFromPDF
   */
  const handleUpdatePDFData = ([
    {
      data: { languages },
    },
    {
      data: { language_levels },
    },
    {
      data: { work_experiences },
    },
  ]) => {
    hasError && setHasError(false);
    setPDFData({ languages, language_levels, work_experiences });
  };

  /**
   * Will request data concurrently if `language` is presented or changed
   * and set the results to `pdfData` state
   */
  useEffect(() => {
    if (language) {
      const lang = `?lang=${language}`;
      // hops adds a polyfill for `Promise` :) (so this code works even in IE-11)
      // Improve this part of code (extract axios to an unique instance to share among all files)
      Promise.all([
        axios.get(`${externalUrl}/${PDF_ENDPOINT_BASE}/languages${lang}`, {
          withCredentials: true,
        }),
        axios.get(
          `${externalUrl}/${PDF_ENDPOINT_BASE}/language_levels${lang}`,
          {
            withCredentials: true,
          },
        ),
        axios.get(
          `${externalUrl}/${PDF_ENDPOINT_BASE}/work_experiences${lang}`,
          {
            withCredentials: true,
          },
        ),
      ])
        .then(handleUpdatePDFData)
        .catch(() => setHasError(true));
    }
  }, [language]);

  const memoizedValues = useMemo(() => ({ ...pdfData, hasError }), [
    pdfData,
    hasError,
  ]);

  return (
    <PDFContext.Provider value={memoizedValues}>{children}</PDFContext.Provider>
  );
};

PDFProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { PDFProvider, usePDF };
